import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  IconButton,
  Pagination,
  Skeleton,
  Table as ChakraTable,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import {
  LuArrowDown,
  LuArrowUp,
  LuChevronLeft,
  LuChevronRight,
  LuChevronsUpDown,
  LuMoreVertical,
  LuSearchX,
  LuSettings2,
  LuX,
} from "react-icons/lu";
import { useEffect, useRef, useState } from "react";
import { defaultActions } from "./defaultActions";
import TableControls from "./TableControls";
import ConfigColumnsDrawer from "../drawers/ConfigColumnsDrawer";
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from "../chakra/menu";
import { NativeSelectField, NativeSelectRoot } from "../chakra/native-select";
import { PAGE_SIZE_OPTIONS, storePageSize } from "./pageSize";
import { Checkbox } from "../chakra/checkbox";

type HiddenColumnsModule =
  | "applicants"
  | "workOrders"
  | "vouchers"
  | "replenishments"
  | "localProducts"
  | "erpProducts"
  | "aliasProducts"
  | "kitProducts"
  | "clientEntities"
  | "activityLabels"
  | "productAdjustments";

export interface Action {
  label: string | ((item: Record<string, unknown>) => string);
  onClick: (item: Record<string, unknown>) => void;
  icon: React.ReactNode;
}

export interface TableHeader<T> {
  label: string;
  itemLabel: ((item: T) => React.ReactNode) | string;
  sortable?: boolean;
  sortKey?: string;
  align?: "start" | "center" | "end";
  width?: string | number;
}
export interface TableProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  data?: T[] | null;
  headers?: TableHeader<T>[];
  extractItemKey: (item: T) => string;
  onChangePage?: (page: number) => void;
  page?: number;
  pages?: number;
  pageSize?: number;
  onChangePageSize?: (pageSize: number) => void;
  totalItems?: number;
  itemsLabel?: string;
  showDefaultActions?: boolean;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  actions?: Action[];
  isLoading?: boolean;
  showPagination?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  showDisabledCheck?: boolean;
  onSearch?: (search: string) => void;
  onShowDisabledChange?: (showDisabled: boolean) => void;
  showConfigColumns?: boolean;
  hiddenColumnsKey?: HiddenColumnsModule;
  onSortChange?: (column: string, direction: "ASC" | "DESC") => void;
  headerActions?: React.ReactNode;
  filters?: React.ReactNode;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  selectedKeys?: string[];
  onSelectionChange?: (items: T[], selected: boolean) => void;
  onClearSelection?: () => void;
  bulkActions?: React.ReactNode;
}

const SKELETON_ROWS = 5;

export const Table = <T extends Record<string, unknown>>({
  data = [],
  headers = [],
  onChangePage,
  page,
  pages,
  pageSize,
  onChangePageSize,
  totalItems,
  itemsLabel,
  showDefaultActions = true,
  extractItemKey,
  onEdit,
  onDelete,
  actions,
  isLoading,
  showSearch = true,
  searchPlaceholder,
  showDisabledCheck = false,
  onSearch,
  onShowDisabledChange,
  showConfigColumns = true,
  hiddenColumnsKey,
  showPagination = true,
  onSortChange,
  headerActions,
  filters,
  hasActiveFilters = false,
  onClearFilters,
  emptyTitle,
  emptyDescription,
  emptyAction,
  selectedKeys,
  onSelectionChange,
  onClearSelection,
  bulkActions,
}: TableProps<T>) => {
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    if (page !== undefined) setCurrentPage(Math.max(page - 1, 0));
  }, [page]);
  const [searchValue, setSearchValue] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasHiddenColumns, setHasHiddenColumns] = useState(false);

  const { open, onOpen, onClose } = useDisclosure();

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    try {
      const storedData = JSON.parse(
        localStorage.getItem("hiddenColumnsId") || "{}",
      );
      if (!hiddenColumnsKey) return {};
      return storedData[hiddenColumnsKey] || {};
    } catch (error) {
      return {};
    }
  });

  const [sortColumn, setSortColumn] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  useEffect(() => {
    try {
      const storedData = JSON.parse(
        localStorage.getItem("hiddenColumnsId") || "{}",
      );
      if (!hiddenColumnsKey) return;
      storedData[hiddenColumnsKey] = columnVisibility;
      localStorage.setItem("hiddenColumnsId", JSON.stringify(storedData));
    } catch (error) {
      console.error("Error saving column visibility to localStorage", error);
    }
  }, [columnVisibility, hiddenColumnsKey]);

  const handleSearch = (search: string) => {
    setSearchValue(search);
    setCurrentPage(0);
    onSearch?.(search);
  };

  const handleClearSearch = () => {
    if (searchValue) handleSearch("");
    onClearFilters?.();
  };

  const isFiltered = searchValue.trim() !== "" || hasActiveFilters;

  const handlePageChange = (selected: number) => {
    setCurrentPage(selected);
    onChangePage?.(selected);
  };

  const handlePageSizeChange = (size: number) => {
    storePageSize(size);
    setCurrentPage(0);
    onChangePageSize?.(size);
  };

  const getSortKey = (header: TableHeader<T>) => {
    if (header.sortKey) return header.sortKey;
    if (typeof header.itemLabel === "string") return header.itemLabel;
    return header.label.replace(/\s+/g, "").toLowerCase();
  };

  const handleSort = (header: TableHeader<T>) => {
    const sortKey = getSortKey(header);
    const isSameColumn = sortColumn === sortKey;
    const direction = isSameColumn
      ? sortDirection === "ASC"
        ? "DESC"
        : "ASC"
      : "ASC";

    setSortColumn(sortKey);
    setSortDirection(direction);
    onSortChange?.(sortKey, direction);
  };

  const visibleHeaders = headers.filter(
    (header) => !columnVisibility[header.label],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
      setHasHiddenColumns(remaining > 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [data, visibleHeaders.length]);
  const hasActionsColumn = !!(actions || showDefaultActions);
  const isSelectable = !!onSelectionChange;
  const selectedSet = new Set(selectedKeys ?? []);
  const selectedCount = selectedSet.size;
  const pageItems = data ?? [];
  const selectedOnPage = pageItems.filter((item) =>
    selectedSet.has(extractItemKey(item)),
  ).length;
  const headerChecked: boolean | "indeterminate" =
    selectedOnPage === 0
      ? false
      : selectedOnPage === pageItems.length
        ? true
        : "indeterminate";
  const totalPages = Math.max(pages ?? 1, 1);
  const columnCount =
    visibleHeaders.length + (hasActionsColumn ? 1 : 0) + (isSelectable ? 1 : 0);
  const shownCount = data?.length ?? 0;
  const rangeStart = currentPage * (pageSize ?? shownCount) + 1;
  const rangeEnd = rangeStart + shownCount - 1;
  const isEmpty = !isLoading && (data?.length ?? 0) === 0;
  const showSkeleton = !!isLoading && (data?.length ?? 0) === 0;

  const rowActions = (item: T) => {
    const list = actions
      ? actions
      : defaultActions.filter(
          (action) => !(action.label === "Eliminar" && !onDelete),
        );

    return list.map((action) => {
      const label =
        typeof action.label === "function" ? action.label(item) : action.label;
      const onClick = actions
        ? () => action.onClick(item)
        : label === "Editar"
          ? () => onEdit?.(item)
          : label === "Eliminar"
            ? () => onDelete?.(item)
            : undefined;
      return { label, icon: action.icon, onClick };
    });
  };

  return (
    <Box
      w="100%"
      bg="bg.panel"
      rounded="xl"
      shadow="card"
      overflow="hidden"
    >
      {(showSearch ||
        showDisabledCheck ||
        showConfigColumns ||
        headerActions ||
        filters) && (
        <TableControls
          showSearch={showSearch}
          searchPlaceholder={searchPlaceholder}
          showDisabledCheck={showDisabledCheck}
          onShowDisabledChange={onShowDisabledChange}
          handleSearch={handleSearch}
          searchValue={searchValue}
          filters={filters}
          rightActions={
            <>
              {showConfigColumns && (
                <Button variant="outline" size="sm" onClick={onOpen}>
                  <LuSettings2 />
                  Columnas
                </Button>
              )}
              {headerActions}
            </>
          }
        />
      )}

      {isSelectable && selectedCount > 0 && (
        <Flex
          px={{ base: 3, md: 4 }}
          py={2}
          gap={3}
          align="center"
          justify="space-between"
          wrap="wrap"
          bg="brand.subtle"
          borderBottomWidth="1px"
          borderColor="brand.muted"
          role="region"
          aria-label="Acciones sobre la selección"
        >
          <HStack gap={2}>
            <Text fontSize="sm" fontWeight="semibold" color="brand.fg">
              {selectedCount}{" "}
              {selectedCount === 1 ? "seleccionado" : "seleccionados"}
            </Text>
            {onClearSelection && (
              <Button
                size="xs"
                variant="ghost"
                colorPalette="brand"
                onClick={onClearSelection}
              >
                <LuX />
                Quitar selección
              </Button>
            )}
          </HStack>
          <HStack gap={2} flexWrap="wrap">
            {bulkActions}
          </HStack>
        </Flex>
      )}

      <ChakraTable.ScrollArea position="relative" ref={scrollRef}>
        <ChakraTable.Root size="md" variant="line">
          <ChakraTable.Header bg="bg.subtle">
            <ChakraTable.Row>
              {isSelectable && (
                <ChakraTable.ColumnHeader width="1%" py={2.5}>
                  <Checkbox
                    size="sm"
                    colorPalette="brand"
                    aria-label="Seleccionar todos los de esta página"
                    checked={headerChecked}
                    disabled={pageItems.length === 0}
                    onCheckedChange={() =>
                      onSelectionChange(pageItems, headerChecked !== true)
                    }
                  />
                </ChakraTable.ColumnHeader>
              )}
              {visibleHeaders.map((header) => {
                const sortKey = getSortKey(header);
                const isSorted = sortColumn === sortKey;
                return (
                  <ChakraTable.ColumnHeader
                    key={header.label}
                    cursor={header.sortable ? "pointer" : "default"}
                    userSelect="none"
                    onClick={
                      header.sortable ? () => handleSort(header) : undefined
                    }
                    fontSize="xs"
                    fontWeight="semibold"
                    letterSpacing="wider"
                    textTransform="uppercase"
                    color={isSorted ? "fg" : "fg.muted"}
                    whiteSpace="nowrap"
                    textAlign={header.align}
                    width={header.width}
                    py={2.5}
                  >
                    <HStack
                      gap={1}
                      display="inline-flex"
                      justify={header.align === "end" ? "flex-end" : undefined}
                    >
                      <span>{header.label}</span>
                      {header.sortable &&
                        (isSorted ? (
                          sortDirection === "ASC" ? (
                            <LuArrowUp />
                          ) : (
                            <LuArrowDown />
                          )
                        ) : (
                          <Box as="span" color="fg.subtle">
                            <LuChevronsUpDown />
                          </Box>
                        ))}
                    </HStack>
                  </ChakraTable.ColumnHeader>
                );
              })}
              {hasActionsColumn && (
                <ChakraTable.ColumnHeader
                  width="1%"
                  py={2.5}
                  position="sticky"
                  right={0}
                  bg="bg.subtle"
                  zIndex={1}
                  borderLeftWidth={hasHiddenColumns ? "1px" : 0}
                  borderLeftColor="border"
                  boxShadow={hasHiddenColumns ? "stickyEdge" : "none"}
                  transition="box-shadow 0.15s ease-out"
                >
                  <Box srOnly>Acciones</Box>
                </ChakraTable.ColumnHeader>
              )}
            </ChakraTable.Row>
          </ChakraTable.Header>
          <ChakraTable.Body>
            {data?.map((item) => {
              const isSelected = selectedSet.has(extractItemKey(item));
              return (
                <ChakraTable.Row
                  key={extractItemKey?.(item)}
                  _hover={{ bg: isSelected ? "brand.subtle" : "bg.subtle" }}
                  bg={isSelected ? "brand.subtle" : undefined}
                  aria-selected={isSelectable ? isSelected : undefined}
                >
                  {isSelectable && (
                    <ChakraTable.Cell py={3}>
                      <Checkbox
                        size="sm"
                        colorPalette="brand"
                        aria-label="Seleccionar fila"
                        checked={isSelected}
                        onCheckedChange={(e) =>
                          onSelectionChange([item], e.checked === true)
                        }
                      />
                    </ChakraTable.Cell>
                  )}
                  {visibleHeaders.map((header) => (
                    <ChakraTable.Cell
                      key={header.label}
                      textAlign={header.align}
                      py={3}
                      fontSize="sm"
                    >
                      {typeof header.itemLabel === "string"
                        ? (item[header.itemLabel] as React.ReactNode)
                        : header.itemLabel(item)}
                    </ChakraTable.Cell>
                  ))}

                  {hasActionsColumn && (
                    <ChakraTable.Cell
                      textAlign="end"
                      py={1.5}
                      position="sticky"
                      right={0}
                      bg={isSelected ? "brand.subtle" : "bg.panel"}
                      zIndex={1}
                      borderLeftWidth={hasHiddenColumns ? "1px" : 0}
                      borderLeftColor="border"
                      boxShadow={hasHiddenColumns ? "stickyEdge" : "none"}
                      transition="box-shadow 0.15s ease-out"
                    >
                      <MenuRoot positioning={{ placement: "bottom-end" }}>
                        <MenuTrigger asChild>
                          <IconButton
                            aria-label="Acciones"
                            variant="ghost"
                            size="sm"
                            color="fg.muted"
                          >
                            <LuMoreVertical />
                          </IconButton>
                        </MenuTrigger>
                        <MenuContent minW="13rem">
                          {rowActions(item).map((action) => (
                            <MenuItem
                              key={action.label}
                              value={action.label}
                              onClick={action.onClick}
                              cursor="pointer"
                            >
                              <Box
                                color="fg.muted"
                                display="inline-flex"
                                css={{
                                  "& svg": {
                                    width: "16px",
                                    height: "16px",
                                    strokeWidth: 1.5,
                                  },
                                }}
                              >
                                {action.icon}
                              </Box>
                              {action.label}
                            </MenuItem>
                          ))}
                        </MenuContent>
                      </MenuRoot>
                    </ChakraTable.Cell>
                  )}
                </ChakraTable.Row>
              );
            })}

            {showSkeleton &&
              Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                <ChakraTable.Row key={`skeleton-${rowIndex}`} aria-hidden>
                  {Array.from({ length: columnCount }, (_, cellIndex) => (
                    <ChakraTable.Cell key={cellIndex} py={4}>
                      <Skeleton
                        h="4"
                        rounded="sm"
                        w={cellIndex === columnCount - 1 ? "6" : "70%"}
                      />
                    </ChakraTable.Cell>
                  ))}
                </ChakraTable.Row>
              ))}

            {isEmpty && (
              <ChakraTable.Row>
                <ChakraTable.Cell colSpan={columnCount} py={12}>
                  <VStack gap={2} textAlign="center" role="status">
                    <Box color="fg.subtle">
                      <LuSearchX size={28} />
                    </Box>
                    <Text fontWeight="semibold">
                      {isFiltered
                        ? "Sin resultados"
                        : emptyTitle ??
                          `Aún no hay ${itemsLabel ?? "registros"}`}
                    </Text>
                    <Text fontSize="sm" color="fg.muted" maxW="md">
                      {isFiltered
                        ? `No encontramos ${itemsLabel ?? "registros"} con esa búsqueda.`
                        : emptyDescription}
                    </Text>
                    {isFiltered ? (
                      <Button
                        size="sm"
                        variant="outline"
                        mt={2}
                        onClick={handleClearSearch}
                      >
                        Limpiar búsqueda
                      </Button>
                    ) : (
                      emptyAction && <Box mt={2}>{emptyAction}</Box>
                    )}
                  </VStack>
                </ChakraTable.Cell>
              </ChakraTable.Row>
            )}
          </ChakraTable.Body>
        </ChakraTable.Root>
      </ChakraTable.ScrollArea>

      <ConfigColumnsDrawer
        isOpen={open}
        onClose={onClose}
        headers={headers.map((header) => header.label)}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
      />

      {showPagination && (
        <Flex
          px={{ base: 4, md: 6 }}
          py={{ base: 3, md: 4 }}
          justify={{ base: "center", sm: "space-between" }}
          align="center"
          gap={{ base: 2, md: 4 }}
          wrap="wrap"
          borderTopWidth="1px"
          borderColor="border"
        >
          <HStack gap={4} flexWrap="wrap" justify="center">
            <Text color="fg.muted" fontSize="sm" data-tabular>
              {totalItems !== undefined ? (
                shownCount === 0 ? (
                  <>
                    Mostrando 0 de {totalItems} {itemsLabel ?? "registros"}
                  </>
                ) : (
                  <>
                    Mostrando {rangeStart}–{rangeEnd} de {totalItems}{" "}
                    {itemsLabel ?? "registros"}
                  </>
                )
              ) : (
                <>Mostrando {shownCount} resultado(s)</>
              )}
            </Text>
            {onChangePageSize && (
              <NativeSelectRoot size="sm" w="auto">
                <NativeSelectField
                  aria-label="Registros por página"
                  value={pageSize ?? PAGE_SIZE_OPTIONS[0]}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  color="fg.muted"
                  fontSize="sm"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size} por página
                    </option>
                  ))}
                </NativeSelectField>
              </NativeSelectRoot>
            )}
          </HStack>
          <Pagination.Root
            count={totalPages}
            pageSize={1}
            page={currentPage + 1}
            onPageChange={(e) => handlePageChange(e.page - 1)}
            siblingCount={1}
          >
            <ButtonGroup variant="outline" size="sm" gap={1}>
              <Pagination.PrevTrigger asChild>
                <Button aria-label="Anterior">
                  <LuChevronLeft />
                  <Box as="span" display={{ base: "none", sm: "inline" }}>
                    Anterior
                  </Box>
                </Button>
              </Pagination.PrevTrigger>
              <Pagination.Items
                render={(page) => (
                  <IconButton
                    aria-label={`Página ${page.value}`}
                    variant={{ base: "outline", _selected: "solid" }}
                    colorPalette="brand"
                  >
                    {page.value}
                  </IconButton>
                )}
              />
              <Pagination.NextTrigger asChild>
                <Button aria-label="Siguiente">
                  <Box as="span" display={{ base: "none", sm: "inline" }}>
                    Siguiente
                  </Box>
                  <LuChevronRight />
                </Button>
              </Pagination.NextTrigger>
            </ButtonGroup>
          </Pagination.Root>
        </Flex>
      )}
    </Box>
  );
};
